import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Tabs, Tab } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";
import FinancialFunnel from "../components/FinancialFunnel";
import UserJourney from "../components/UserJourney";
import RequestLogs from "../components/RequestLogs";
import UserManagement from "../components/UserManagement";
import BoostManagement from "../components/BoostManagement";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

// Admin dashboard component to display various CRO analytics
const Admin = () => {
  const [loading, setLoading] = useState(true);
  const [toolStats, setToolStats] = useState([]);
  const [boostStats, setBoostStats] = useState([]);
  const [toolSuccessRates, setToolSuccessRates] = useState([]);
  const [platformStats, setPlatformStats] = useState({
    platforms: [],
    subscriptions: [],
    platformSubscriptions: [],
  });
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    dailyGrowth: [],
  });
  const [activeTab, setActiveTab] = useState("financial");

  useEffect(() => {
    // Use Promise.all to fetch all endpoints concurrently.
    const fetchData = async () => {
      try {
        const [
          toolStatsResponse,
          boostStatsResponse,
          toolSuccessRatesResponse,
          platformStatsResponse,
          userStatsResponse,
        ] = await Promise.all([
          axiosClient.get("https://api.viralgpt.com/admin/tool-stats"),
          axiosClient.get("https://api.viralgpt.com/admin/boost-stats"),
          axiosClient.get("https://api.viralgpt.com/admin/tool-success-rates"),
          axiosClient.get("https://api.viralgpt.com/admin/platform-stats"),
          axiosClient.get("https://api.viralgpt.com/admin/user-stats"),
        ]);
        setToolStats(toolStatsResponse.data);
        setBoostStats(boostStatsResponse.data);
        setToolSuccessRates(toolSuccessRatesResponse.data);
        setPlatformStats(platformStatsResponse.data);
        setUserStats(userStatsResponse.data);
      } catch (error) {
        console.error("Error fetching admin logs data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const userGrowthData = {
    labels: userStats.dailyGrowth.map((item) =>
      new Date(item.date).toLocaleDateString(),
    ),
    datasets: [
      {
        label: "New Users",
        data: userStats.dailyGrowth.map((item) => item.count),
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  };

  const userGrowthOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Daily New Users (Last 30 Days)",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of New Users",
        },
      },
    },
    maintainAspectRatio: false,
  };

  const transformBoostStats = (boostStats) => {
    const platformActionStats = {};

    boostStats.forEach(({ eventName, count }) => {
      // Parse the event name: BOOST_ORDER_instagram_followers_5_START
      const parts = eventName.split("_");
      if (parts.length >= 4) {
        const platform = parts[2]; // instagram, tiktok, etc.
        const action = parts[3]; // followers, likes, etc.
        const status = parts[parts.length - 1]; // START, SUCCESS, FAILURE

        // Initialize nested objects if they don't exist
        platformActionStats[platform] = platformActionStats[platform] || {};
        platformActionStats[platform][action] = platformActionStats[platform][
          action
        ] || {
          starts: 0,
          successes: 0,
          failures: 0,
        };

        // Increment the appropriate counter
        if (status === "START")
          platformActionStats[platform][action].starts += count;
        else if (status === "SUCCESS")
          platformActionStats[platform][action].successes += count;
        else if (status === "FAILURE")
          platformActionStats[platform][action].failures += count;
      }
    });

    return platformActionStats;
  };

  // Prepare data for the "Tool Stats" chart (grouped bar chart)
  const toolStatsData = {
    labels: toolStats.map((item) => item.tool),
    datasets: [
      {
        label: "Starts",
        data: toolStats.map((item) => item.starts),
        backgroundColor: "rgba(153,102,255,0.6)",
      },
      {
        label: "Successes",
        data: toolStats.map((item) => item.successes),
        backgroundColor: "rgba(75,192,192,0.6)",
      },
      {
        label: "Failures",
        data: toolStats.map((item) => item.failures),
        backgroundColor: "rgba(255,99,132,0.6)",
      },
    ],
  };

  // Prepare data for the "Boost Stats" chart
  const boostStatsData = {
    labels: Object.keys(transformBoostStats(boostStats)).flatMap((platform) =>
      Object.keys(transformBoostStats(boostStats)[platform]).map(
        (action) => `${platform}\n${action}`,
      ),
    ),
    datasets: [
      {
        label: "Starts",
        data: Object.entries(transformBoostStats(boostStats)).flatMap(
          ([platform, actions]) =>
            Object.values(actions).map((stats) => stats.starts),
        ),
        backgroundColor: "rgba(153,102,255,0.6)",
      },
      {
        label: "Successes",
        data: Object.entries(transformBoostStats(boostStats)).flatMap(
          ([platform, actions]) =>
            Object.values(actions).map((stats) => stats.successes),
        ),
        backgroundColor: "rgba(75,192,192,0.6)",
      },
      {
        label: "Failures",
        data: Object.entries(transformBoostStats(boostStats)).flatMap(
          ([platform, actions]) =>
            Object.values(actions).map((stats) => stats.failures),
        ),
        backgroundColor: "rgba(255,99,132,0.6)",
      },
    ],
  };

  const boostChartOptions = {
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Count",
        },
      },
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: "Boost Orders by Platform and Action Type",
      },
      legend: {
        position: "top",
      },
    },
    maintainAspectRatio: false,
  };

  // Prepare data for the "Tool Success Rates" chart
  const toolSuccessRatesData = {
    labels: toolSuccessRates.map((item) => item.tool),
    datasets: [
      {
        label: "Success Rate (%)",
        data: toolSuccessRates.map((item) => item.success_rate),
        backgroundColor: "rgba(75,192,192,0.6)",
      },
    ],
  };

  // Basic chart options to ensure y-axis starts from 0
  const chartOptions = {
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  if (loading) {
    return (
      <Container className="py-4">
        <p>Loading admin data...</p>
      </Container>
    );
  }

  const platformDistributionData = {
    labels: platformStats?.platforms?.map((item) => item.platform) || [],
    datasets: [
      {
        label: "Number of Profiles",
        data: platformStats?.platforms?.map((item) => item.count) || [],
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
        ],
      },
    ],
  };

  const subscriptionDistributionData = {
    labels:
      platformStats?.subscriptions?.map(
        (item) => `${item.planName} (${item.planInterval})`,
      ) || [],
    datasets: [
      {
        label: "Active Subscriptions",
        data: platformStats?.subscriptions?.map((item) => item.count) || [],
        backgroundColor: [
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(255, 99, 132, 0.6)",
        ],
      },
    ],
  };

  const platformSubscriptionData = {
    labels: [
      ...new Set(
        platformStats?.platformSubscriptions?.map((item) => item.platform) ||
          [],
      ),
    ],
    datasets: [
      {
        label: "Basic Monthly",
        data: [
          ...new Set(
            platformStats?.platformSubscriptions?.map(
              (item) => item.platform,
            ) || [],
          ),
        ].map((platform) => {
          const match = platformStats?.platformSubscriptions?.find(
            (item) =>
              item.platform === platform &&
              item.planName === "basic" &&
              item.planInterval === "monthly",
          );
          return match ? match.count : 0;
        }),
        backgroundColor: "rgba(255, 99, 132, 0.6)",
      },
      {
        label: "Basic Annual",
        data: [
          ...new Set(
            platformStats?.platformSubscriptions?.map(
              (item) => item.platform,
            ) || [],
          ),
        ].map((platform) => {
          const match = platformStats?.platformSubscriptions?.find(
            (item) =>
              item.platform === platform &&
              item.planName === "basic" &&
              item.planInterval === "annual",
          );
          return match ? match.count : 0;
        }),
        backgroundColor: "rgba(54, 162, 235, 0.6)",
      },
      {
        label: "Pro Monthly",
        data: [
          ...new Set(
            platformStats?.platformSubscriptions?.map(
              (item) => item.platform,
            ) || [],
          ),
        ].map((platform) => {
          const match = platformStats?.platformSubscriptions?.find(
            (item) =>
              item.platform === platform &&
              item.planName === "pro" &&
              item.planInterval === "monthly",
          );
          return match ? match.count : 0;
        }),
        backgroundColor: "rgba(255, 206, 86, 0.6)",
      },
      {
        label: "Pro Annual",
        data: [
          ...new Set(
            platformStats?.platformSubscriptions?.map(
              (item) => item.platform,
            ) || [],
          ),
        ].map((platform) => {
          const match = platformStats?.platformSubscriptions?.find(
            (item) =>
              item.platform === platform &&
              item.planName === "pro" &&
              item.planInterval === "annual",
          );
          return match ? match.count : 0;
        }),
        backgroundColor: "rgba(75, 192, 192, 0.6)",
      },
    ],
  };

  const platformChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Platform Distribution",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Profiles",
        },
      },
    },
    maintainAspectRatio: false,
  };

  const subscriptionChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Subscription Distribution",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Subscriptions",
        },
      },
    },
    maintainAspectRatio: false,
  };

  const platformSubscriptionOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Subscription Distribution by Platform",
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Subscriptions",
        },
      },
    },
    maintainAspectRatio: false,
  };

  // Render the Analytics Dashboard content
  const renderAnalyticsDashboard = () => (
    <>
      <Row className="mb-4">
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title>Total Users</Card.Title>
              <h2 className="display-4 text-center">
                {userStats.totalUsers.toLocaleString()}
              </h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={8}>
          <Card>
            <Card.Body>
              <Card.Title>User Growth</Card.Title>
              <div style={{ height: "400px" }}>
                <Bar data={userGrowthData} options={userGrowthOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title>Platform Distribution</Card.Title>
              <div style={{ height: "400px" }}>
                <Bar
                  data={platformDistributionData}
                  options={platformChartOptions}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title>Subscription Distribution</Card.Title>
              <div style={{ height: "400px" }}>
                <Bar
                  data={subscriptionDistributionData}
                  options={subscriptionChartOptions}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title>Platform Subscription Distribution</Card.Title>
              <div style={{ height: "400px" }}>
                <Bar
                  data={platformSubscriptionData}
                  options={platformSubscriptionOptions}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tool Stats Chart */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Card.Title>Tool Usage</Card.Title>
              <Bar data={toolStatsData} options={chartOptions} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tool Success Rates */}
      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Body>
              <Card.Title>Tool Success</Card.Title>
              <Bar data={toolSuccessRatesData} options={chartOptions} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Card.Title>Boost Order Analysis</Card.Title>
              <div style={{ height: "400px" }}>
                {" "}
                {/* Fixed height container for the chart */}
                <Bar data={boostStatsData} options={boostChartOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <Container className="py-4">
      <div className="d-flex align-items-center mb-4">
        <h2 className="mb-0 me-auto">ViralGPT Admin View</h2>
        <img
          src="/images/luke_pfp.jpg"
          alt="Luke"
          className="rounded-circle me-2"
          style={{ width: "60px", height: "60px" }}
        />
        <img
          src="/images/jonas_pfp.png"
          alt="Jonas"
          className="rounded-circle me-2"
          style={{ width: "60px", height: "60px" }}
        />
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="financial" title="Revenue 💰">
          <FinancialFunnel />
        </Tab>
        <Tab eventKey="journey" title="Clicks 🚶">
          <UserJourney />
        </Tab>
        <Tab eventKey="logs" title="Logs 📋">
          <RequestLogs />
        </Tab>
        <Tab eventKey="users" title="Users 👥">
          <UserManagement />
        </Tab>
        <Tab eventKey="boosts" title="Boosts 🚀">
          <BoostManagement />
        </Tab>
        <Tab eventKey="analytics" title="Dashboard 🔍">
          {renderAnalyticsDashboard()}
        </Tab>
      </Tabs>
    </Container>
  );
};

export default Admin;
